import React, { useEffect, useMemo, useState } from 'react';
import { LoaderCircle, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import FormHeader from '../../../components/form/FormHeader';
import { Button } from '../../../components/button';
import { Card } from '../../../components/card';
import './CreateEGiftCardPage.css';
import { EGiftCardForm } from '../components/form/EGiftCardForm';
import { useGetEGiftCardDetail } from '../hooks/useGetEGiftCardDetail';
import { useUpdateEGiftCard } from '../hooks/useUpdateEGiftCard';
import type { CreateEGiftCardRequest, EGiftCardFormData } from '../types/eGiftCardForm.types';
import { mapEGiftCardDetailToFormData } from '../utils/eGiftCardFormMapper';

const UPDATE_E_GIFT_CARD_FORM_ID = 'update-e-gift-card-form';

const UpdateEGiftCardPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();

    const { eGiftCardDetail, isLoading: isLoadingDetail, error, refetch } = useGetEGiftCardDetail(id);
    const { updateEGiftCard, isLoading: isUpdating } = useUpdateEGiftCard();
    const [isSubmissionBlocked, setIsSubmissionBlocked] = useState(false);
    const initialValues = useMemo(
        () => eGiftCardDetail ? mapEGiftCardDetailToFormData(eGiftCardDetail) : null,
        [eGiftCardDetail],
    );

    useEffect(() => {
        if (!id) {
            navigate('/e-gift-card', { replace: true });
            return;
        }

    }, [id, navigate]);

    const onSubmit = async (data: EGiftCardFormData) => {
        if (!id) return;

        const request: CreateEGiftCardRequest = {
            merchant_brand_id: data.merchantBrandId,
            gift_cards: data.giftCardItems || [],
            gift_card_ids: data.giftCardIds,
            valid_from: data.validFrom,
            valid_to: data.validTo,
            timezone: data.timezone,
            is_alert_low_stock: data.isAlertLowStock,
        };

        await updateEGiftCard(id, request);
    };

    return (
        <Card className="border-none rounded-none h-full flex flex-col bg-[#F9FAFB] dark:bg-muted/50">
            <FormHeader
                onBack={() => navigate('/e-gift-card')}
                title={t('createEGiftCard.editTitle') || 'Update Brand E-Gift Card'}
                subtitle=""
                backLabel={t('common.back') || 'Back'}
                actions={(
                    <Button
                        className="rounded-lg px-6 py-2 text-sm font-semibold bg-[#00C49A] hover:bg-[#00C49A]/90 text-white"
                        type="submit"
                        form={UPDATE_E_GIFT_CARD_FORM_ID}
                        disabled={
                            isUpdating ||
                            isLoadingDetail ||
                            !eGiftCardDetail ||
                            isSubmissionBlocked
                        }
                    >
                        {isUpdating
                            ? <LoaderCircle className="animate-spin" aria-hidden="true" />
                            : <Save aria-hidden="true" />}
                        {isUpdating
                            ? t('createEGiftCard.updating') || 'Updating...'
                            : t('createEGiftCard.saveChanges') || 'Save Changes'}
                    </Button>
                )}
            />

            <div className="p-6">
                {isLoadingDetail && (
                    <div className="flex h-64 items-center justify-center">
                        <div className="text-center">
                            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-[#00C49A]" />
                            <p className="text-gray-600 dark:text-gray-400">
                                {t('common.loading') || 'Loading...'}
                            </p>
                        </div>
                    </div>
                )}

                {!isLoadingDetail && (error || !eGiftCardDetail || !initialValues) && (
                    <div className="flex h-64 flex-col items-center justify-center gap-4 text-center">
                        <p className="text-gray-600 dark:text-gray-400">
                            {t('createEGiftCard.loadDetailFailed')}
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => void refetch()}
                        >
                            {t('ui.retry')}
                        </Button>
                    </div>
                )}

                {!isLoadingDetail && eGiftCardDetail && initialValues && (
                    <EGiftCardForm
                        formId={UPDATE_E_GIFT_CARD_FORM_ID}
                        isEditMode
                        currentEGiftCardId={id}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onSubmissionBlockedChange={setIsSubmissionBlocked}
                    />
                )}
            </div>
        </Card>
    );
};

export default UpdateEGiftCardPage;
